import { ArrowRight, type LucideIcon } from "lucide-react";
import type { ComponentPropsWithRef, ReactNode } from "react";
import { cn } from "@/lib/utils";

export type OptionCardTone = "primary" | "accent" | "success" | "error";

const toneClass: Record<
  OptionCardTone,
  { card: string; icon: string; iconRounded?: string }
> = {
  primary: {
    card: "hover:border-primary-500/40",
    icon: "bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-600/15",
    iconRounded: "rounded-lg",
  },
  accent: {
    card: "hover:border-accent-500/40",
    icon: "bg-accent-50 text-accent-600 ring-1 ring-inset ring-accent-600/15",
    iconRounded: "rounded-lg",
  },
  success: {
    card: "hover:border-success-500/40",
    icon: "bg-success-50 text-success-600 ring-1 ring-inset ring-success-600/15",
    iconRounded: "rounded-lg",
  },
  error: {
    card: "hover:border-error-500/40",
    icon: "bg-error-50 text-error-600 ring-1 ring-inset ring-error-600/15",
    iconRounded: "rounded-lg",
  },
};

export interface OptionCardLinkRenderProps {
  href: string;
  className: string;
  children: ReactNode;
}

export type OptionCardProps = Omit<ComponentPropsWithRef<"button">, "children" | "title"> & {
  icon: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  tone?: OptionCardTone;
  to?: string;
  renderLink?: (props: OptionCardLinkRenderProps) => ReactNode;
  onClick?: () => void;
  iconClassName?: string;
  surface?: "default" | "elevated";
};

const surfaceClass = {
  default: "border border-surface-200 bg-surface-0",
  elevated: "border border-surface-200 bg-surface-0 shadow-card",
};

export function OptionCard({
  icon: Icon,
  title,
  description,
  tone = "primary",
  to,
  renderLink,
  onClick,
  className,
  iconClassName,
  surface = "default",
  ...rest
}: OptionCardProps) {
  const toneStyles = toneClass[tone];
  const content = (
    <>
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center",
          toneStyles.iconRounded,
          iconClassName ?? toneStyles.icon,
        )}
      >
        <Icon className="h-[18px] w-[18px]" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-surface-900">{title}</p>
        {description ? (
          <p className="text-[13px] text-surface-500">{description}</p>
        ) : null}
      </div>
      <ArrowRight
        className="h-4 w-4 shrink-0 -translate-x-1 text-surface-400 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
        aria-hidden
      />
    </>
  );

  const cardClass = cn(
    "group flex items-center gap-3 rounded-xl p-3.5 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-px hover:shadow-elevated",
    surfaceClass[surface],
    toneStyles.card,
    className,
  );

  if (to && renderLink) {
    return renderLink({ href: to, className: cardClass, children: content });
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(cardClass, "text-left")}
      {...rest}
    >
      {content}
    </button>
  );
}
